import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';

const DEVICE_ID_KEY = 'examflow_device_id';
const LAST_SYNC_KEY = 'examflow_last_sync';

function getDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

function getLastSyncTime(): number | null {
  const stored = localStorage.getItem(LAST_SYNC_KEY);
  return stored ? parseInt(stored, 10) : null;
}

function setLastSyncTime(time: number) {
  localStorage.setItem(LAST_SYNC_KEY, String(time));
}

export type SyncStatus = 'synced' | 'syncing' | 'error';

export interface SyncState {
  status: SyncStatus;
  lastSyncTime: number | null;
  error: string | null;
}

export interface UseSyncStatusReturn {
  status: SyncStatus;
  lastSyncTime: number | null;
  error: string | null;
  triggerSync: () => Promise<void>;
  isSyncing: boolean;
}

/**
 * Hook that tracks and manages multi-device sync status.
 * Uses api.sync.status() to get sync records for the current device,
 * stores lastSyncTime in localStorage, and provides a manual sync trigger.
 */
export function useSyncStatus(): UseSyncStatusReturn {
  const deviceId = useRef(getDeviceId()).current;
  const [state, setState] = useState<SyncState>({
    status: 'synced',
    lastSyncTime: getLastSyncTime(),
    error: null,
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const syncInProgress = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await api.sync.status(deviceId);
      const serverSyncTime = result?.lastSyncTime ? new Date(result.lastSyncTime).getTime() : null;

      // Update last sync time if server has newer data
      if (serverSyncTime && (!state.lastSyncTime || serverSyncTime > state.lastSyncTime)) {
        setLastSyncTime(serverSyncTime);
        setState((prev) => ({ ...prev, lastSyncTime: serverSyncTime }));
      }

      setState((prev) => ({ ...prev, status: 'synced', error: null }));
    } catch {
      // Silently handle status fetch errors — not critical
    }
  }, [deviceId, state.lastSyncTime]);

  // Initial status fetch and periodic refresh
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60_000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const triggerSync = useCallback(async () => {
    if (syncInProgress.current) return;
    syncInProgress.current = true;
    setIsSyncing(true);
    setState((prev) => ({ ...prev, status: 'syncing', error: null }));

    try {
      const now = Date.now();
      setLastSyncTime(now);
      setState((prev) => ({ ...prev, lastSyncTime: now, status: 'synced' }));
      await fetchStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: message,
      }));
    } finally {
      syncInProgress.current = false;
      setIsSyncing(false);
    }
  }, [fetchStatus]);

  return {
    status: state.status,
    lastSyncTime: state.lastSyncTime,
    error: state.error,
    triggerSync,
    isSyncing,
  };
}
